import type { TranslationKeys } from '../types';

const zh_CN: TranslationKeys = {
  nav: {
    home: '首页',
    blockExplorer: '浏览器',
    swap: '兑换',
    exchange: '跨链兑换',
    docs: '文档',
    more: '更多',
    close: '关闭',
    bridge: '桥接',
  },
  bridge: {
    title: 'Igra 桥接',
    subtitle: '在 Kaspa 与 Igra（链 {chainId}）之间桥接 KAS',
    tabDeposit: '存入 KAS',
    tabDepositSub: 'KAS → iKAS',
    tabWithdraw: '提取 KAS',
    tabWithdrawSub: 'iKAS → KAS',
    chainKaspa: 'Kaspa',
    chainEvm: 'EVM（Igra）',
    useWallet: '使用钱包',
    gate: {
      installTitle: '连接 KasWare 钱包',
      installDesc: '桥接交易完全在 KasWare 内签名。请安装扩展程序，或在 KasWare Telegram 应用中打开本页面。',
      disconnectedTitle: '钱包已断开连接',
      connectWallet: '连接钱包',
      connectTitle: '连接{chain}',
      connectDesc: '请在 KasWare 中授权{chain}账户以继续。',
      connecting: '连接中…',
      connectChain: '连接{chain}',
    },
    deposit: {
      youSend: '你支付（Kaspa L1）',
      youReceive: '你收到（Igra L2）',
      recipientLabel: '在以下 Igra 地址接收 iKAS',
      fromWallet: '（来自钱包）',
      badAddress: '请输入有效的 EVM 地址——含大写字母的地址必须通过 EIP-55 校验和检查。',
      connectEvm: '连接 EVM 以自动填充',
      connectingEvm: '正在连接 EVM...',
      infoMin: '最小存入额：{min} KAS。',
      infoMining: '交易 ID 在你的浏览器中挖取，需以 97b1 开头——通常只需几秒钟。',
      infoMint: 'Igra 排序器在 Kaspa 上确认 Entry 后铸造 iKAS。',
      phaseConnecting: '正在连接 Kaspa 网络…',
      phaseFetching: '正在获取你的 UTXO…',
      phaseMining: '正在挖取有效的交易 ID…（已尝试 {tries} 次）',
      phaseSigning: '等待你在 KasWare 中签名…',
      phaseBroadcasting: '正在广播 Entry 交易…',
      cancelMining: '取消挖取',
      successTitle: '存入交易已提交',
      successMined: '尝试 {tries} 次后找到有效交易 ID（nonce {nonce}）。',
      kaspaTx: 'Kaspa 交易：',
      prefixWarning:
        '广播后的交易 ID 已不以 97b1 开头，Igra 可能不会入账这笔存入——请保留交易 ID 并联系支持。',
      newDeposit: '再次存入',
      cta: '桥接 KAS 到 Igra',
      ctaAmount: '桥接 {amount} KAS 到 Igra',
    },
    withdraw: {
      youBurn: '你销毁（Igra L2）',
      paidOut: '放款至（Kaspa L1）',
      payoutLabel: 'KAS 收款地址',
      infoLimits: '限额：每笔提取 {min} – {max} iKAS。',
      infoFeeZero: '协议费用：当前为 0。',
      infoFeeQuoted: '协议费用：请求时报价。',
      infoRate: '1 iKAS 销毁后精确放款 1 KAS（无点差）。',
      warningPrefix: '提现',
      warningStrong: '并非即时到账',
      warningRest:
        'iKAS 将在 Igra 上销毁，随后由多签委员会审核请求并在 Kaspa 上释放 KAS。请只发送你愿意等待的金额。',
      checking: '检查中…',
      modalTitle: '确认提现',
      modalBurn: '你销毁',
      modalFee: '协议费用',
      modalPayout: 'KAS 放款至',
      modalAck: '我已知悉放款由多签委员会人工审核，并非即时到账。',
      modalConfirm: '确认销毁 iKAS',
      modalBack: '返回',
      successTitle: '提现请求已在 Igra 上确认',
      requestId: '提现请求 ID：{id}',
      igraTx: 'Igra 交易：',
      successPayout: 'KAS 将发送至：',
      successNote: '多签审核完成后，KAS 将放款至你的收款地址。',
      newWithdrawal: '再次提取',
      requesting: '正在请求提现…',
      cta: '销毁 iKAS 并请求 KAS',
      ctaAmount: '销毁 {amount} iKAS 并请求 KAS',
    },
  },
  home: {
    tagline: 'KasWare 钱包',
    hero: {
      subtitle: '一个自托管钱包，同时管理 TON、Kaspa 与 EVM 资产——私钥始终留在你的设备上。',
      chainsLabel: '已支持网络',
      ctaDownload: '添加到 Chrome',
      ctaTelegram: '在 Telegram 中打开',
      note: '完全免费，无需注册，不用邮箱。',
    },
    pillars: {
      security: {
        title: '安全从设计开始',
        desc: '私钥在本地生成并加密保存。每笔交易签名前都会经过风险检查，并完整展示交易内容。',
      },
      convenience: {
        title: '一个钱包搞定',
        desc: '在 TON、Kaspa 与 EVM 之间收发、兑换、跨链，不必再切换多个钱包和账户。',
      },
      friendly: {
        title: '为真实的人设计',
        desc: '大白话提示、清晰的地址展示、深浅色主题，并内置七种语言。',
      },
      reliable: {
        title: '值得依赖',
        desc: '交易记录公开可查，多节点线路自动切换，单个服务出问题时钱包依然可用。',
      },
    },
    content: {
      downloadTitle: '下载',
      securityTitle: '资产为什么始终属于你',
      p1: 'KasWare 是一款支持 Ton、Kaspa 和 EVM 的自托管钱包。',
      p2: '我们无法访问您的资金。您的私钥由密码加密存储在本地设备上，不会与任何人共享。',
      p3: '所有账户均由您的助记词确定性派生，确保您拥有完全的所有权和控制权。',
    },
  },
  about: {
    contact: '联系我们',
    contacts: {
      twitter: 'Twitter',
      telegram: 'Telegram',
      discord: 'Discord',
      email: '邮箱',
    },
  },
  footer: {
    privacy: '隐私政策',
    copyright: '© {year} Flying Rabbit Tech Ltd 版权所有',
  },
  explorer: {
    title: '交易浏览器',
    subtitle: '按地址搜索 Kaspa 和 KRC-20 交易记录',
    timeRange: '时间范围：',
    to: '至',
    clear: '清除',
    address: '地址：',
    kasTab: 'KAS 转账',
    krc20Tab: 'KRC-20 操作',
    enterAddress: '输入 Kaspa 地址以查看交易记录',
    loading: '加载中...',
    search: {
      placeholder: '输入 Kaspa 地址 (kaspa:q...)',
      button: '搜索',
    },
    tx: {
      loading: '正在加载交易...',
      empty: '该地址未找到交易记录',
      emptyInRange: '在选定时间范围内未找到该地址的交易记录',
      failed: '加载交易失败：{error}',
      retry: '重试',
    },
    krc20: {
      loading: '正在加载 KRC-20 操作...',
      empty: '该地址未找到 KRC-20 操作',
      emptyInRange: '在选定时间范围内未找到该地址的 KRC-20 操作',
      failed: '加载 KRC-20 操作失败：{error}',
      retry: '重试',
    },
  },
  languageNames: {
    en: 'EN',
    id: 'ID',
    fr: 'FR',
    zh_CN: '简体中文',
    zh_TW: '繁體中文',
    es: 'ES',
    th: 'TH',
  },
};

export default zh_CN;
